import { useState } from 'react';
import { companiesService } from '../../services/companies';
import Modal from '../../components/Modal';
import Field from '../../components/Field';
import StatusMessage from '../../components/StatusMessage';
import AddressPicker from '../../components/AddressPicker';
import TrialFields, { defaultTrialForm, parseTrialForm, type TrialFormState } from './TrialFields';
import { BUSINESS_TYPE_OPTIONS, type AdminCompanyCreateResult, type BusinessType, type CreateCompanyPayload, type IndonesiaAddress } from '../../types';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
/** Indonesian mobile: 08xxxxxxxx or 628xxxxxxxx (after stripping spaces, dashes and a leading "+"). */
const PHONE_RE = /^(0|62)8\d{7,12}$/;

const Hint = ({ children }: { children: string }) => <div className="text-xs text-gray-400 -mt-2 mb-3">{children}</div>;
const inputClass = 'w-full border rounded px-3 py-2';

interface FormState {
  companyName: string;
  businessType: BusinessType;
  otherBusinessType: string;
  address: IndonesiaAddress | null;
  addressDetail: string;
  adminName: string;
  adminEmail: string;
  adminPhone: string;
  password: string;
  trial: TrialFormState;
}

const initialForm = (): FormState => ({
  companyName: '',
  businessType: 'tech',
  otherBusinessType: '',
  address: null,
  addressDetail: '',
  adminName: '',
  adminEmail: '',
  adminPhone: '',
  password: '',
  trial: defaultTrialForm(),
});

/** Client-side validation mirroring the POST admin/companies contract; returns the payload or the first error. */
export function buildCreatePayload(f: FormState): { value: CreateCompanyPayload } | { error: string } {
  const companyName = f.companyName.trim();
  if (!companyName) return { error: 'Nama perusahaan wajib diisi.' };
  const otherBusinessType = f.otherBusinessType.trim();
  if (f.businessType === 'other' && !otherBusinessType) return { error: 'Jenis usaha lainnya wajib diisi.' };
  if (!f.address) return { error: 'Alamat (kelurahan) wajib dipilih.' };
  const adminName = f.adminName.trim();
  if (!adminName) return { error: 'Nama PIC wajib diisi.' };
  const adminEmail = f.adminEmail.trim();
  if (!adminEmail) return { error: 'Email wajib diisi.' };
  if (!EMAIL_RE.test(adminEmail)) return { error: 'Format email tidak valid.' };
  const adminPhone = f.adminPhone.replace(/[\s-]/g, '').replace(/^\+/, '');
  if (!adminPhone) return { error: 'No. WhatsApp wajib diisi.' };
  if (!PHONE_RE.test(adminPhone)) return { error: 'No. WhatsApp harus nomor HP Indonesia (contoh 0851xxxx atau 62851xxxx).' };
  if (f.password && f.password.length < 6) return { error: 'Password minimal 6 karakter.' };
  const trial = parseTrialForm(f.trial);
  if ('error' in trial) return trial;

  const payload: CreateCompanyPayload = {
    company_name: companyName,
    business_type: f.businessType,
    indonesia_address_id: f.address.id,
    admin_name: adminName,
    admin_email: adminEmail,
    admin_phone: adminPhone,
    ...trial.value,
  };
  if (f.businessType === 'other') payload.other_business_type = otherBusinessType;
  const addressDetail = f.addressDetail.trim();
  if (addressDetail) payload.address = addressDetail;
  if (f.password) payload.password = f.password;
  return { value: payload };
}

/** Prefer the first server field message (a 422) over the generic envelope message. */
export const firstErrorMessage = (e: unknown): string => {
  const err = e as Error & { fields?: Record<string, string[]> };
  const first = err.fields ? Object.values(err.fields).flat()[0] : undefined;
  return first ?? err.message;
};

const CreateCompanyModal = ({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  /** Called with the created company and the admin email the onboarding mail went to. */
  onCreated: (result: AdminCompanyCreateResult, adminEmail: string) => void;
}) => {
  const [form, setForm] = useState<FormState>(initialForm);
  const [msg, setMsg] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const set = <K extends keyof FormState>(key: K) => (value: FormState[K]) => setForm((f) => ({ ...f, [key]: value }));

  const close = () => {
    if (submitting) return;
    setForm(initialForm());
    setMsg(null);
    onClose();
  };

  const submit = async () => {
    setMsg(null);
    const parsed = buildCreatePayload(form);
    if ('error' in parsed) {
      setMsg(parsed.error);
      return;
    }
    setSubmitting(true);
    try {
      const result = await companiesService.create(parsed.value);
      setForm(initialForm());
      onCreated(result, parsed.value.admin_email);
    } catch (e) {
      setMsg(firstErrorMessage(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} title="Buat Perusahaan (manual)" onClose={close}>
      <div className="space-y-1 text-sm max-h-[70vh] overflow-y-auto pr-1">
        <div className="font-semibold mb-2">Perusahaan</div>
        <Field label="Nama perusahaan">
          <input type="text" className={inputClass} value={form.companyName} onChange={(e) => set('companyName')(e.target.value)} />
        </Field>
        <Field label="Jenis usaha">
          <select className={inputClass} value={form.businessType} onChange={(e) => set('businessType')(e.target.value as BusinessType)}>
            {BUSINESS_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </Field>
        {form.businessType === 'other' && (
          <Field label="Jenis usaha lainnya">
            <input type="text" className={inputClass} value={form.otherBusinessType} onChange={(e) => set('otherBusinessType')(e.target.value)} />
          </Field>
        )}
        <div className="mb-3">
          <span className="block text-xs text-gray-500 mb-1">Alamat (kelurahan)</span>
          <AddressPicker value={form.address} onChange={set('address')} />
        </div>
        <Field label="Alamat detail">
          <textarea className={inputClass} rows={2} value={form.addressDetail} onChange={(e) => set('addressDetail')(e.target.value)} />
        </Field>

        <div className="font-semibold mb-2 pt-2 border-t">Admin / PIC</div>
        <Field label="Nama PIC">
          <input type="text" className={inputClass} value={form.adminName} onChange={(e) => set('adminName')(e.target.value)} />
        </Field>
        <Field label="Email">
          <input type="email" className={inputClass} value={form.adminEmail} onChange={(e) => set('adminEmail')(e.target.value)} />
        </Field>
        <Field label="No. WhatsApp">
          <input type="tel" className={inputClass} placeholder="0851xxxxxxxx" value={form.adminPhone} onChange={(e) => set('adminPhone')(e.target.value)} />
        </Field>
        <Hint>dipakai untuk login OTP di aplikasi</Hint>
        <Field label="Password (opsional)">
          <input type="password" className={inputClass} value={form.password} onChange={(e) => set('password')(e.target.value)} />
        </Field>
        <Hint>kosongkan untuk digenerate otomatis; customer menerima link atur password via email</Hint>

        <div className="font-semibold mb-2 pt-2 border-t">Trial</div>
        <TrialFields form={form.trial} onChange={set('trial')} />

        <StatusMessage error={msg} />
        <button
          onClick={submit}
          disabled={submitting}
          className="bg-primary-600 text-white rounded px-4 py-2 w-full mt-3 disabled:opacity-50"
        >
          {submitting ? 'Membuat…' : 'Buat & kirim email'}
        </button>
      </div>
    </Modal>
  );
};
export default CreateCompanyModal;
